import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import queryString from "query-string";
import moment from "moment";

import Filters from "./Filters";
import ReportService from "../../../services/reportService";

import { formatDate } from "../../../helpers/date";

import PtoBalanceList from "./PtoBalanceList";
import { downloadCSV } from "../utils";
import { PTO_BALANCE_COLUMNS } from "./columns";
import { customToast as toast } from "../../../utils";

const ProjectSummaryPage = ({ codes }) => {
  const [loading, setLoading] = useState(false);
  const [pages, setPages] = useState(0);
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [focusedInput, setFocusedInput] = useState(null);
  const [sorted, setSorted] = useState([{ id: "full_name", desc: false }]);

  const [filters, setFilters] = useState({
    page: 0,
    pageSize: 25,
    start_date: moment().startOf("year"),
    end_date: moment()
  });

  const [data, setData] = useState([]);

  const fetchData = useCallback(
    async (pageNumber, size, ptoSorted) => {
      try {
        setLoading(true);
        const newSorted = ptoSorted || sorted;
        let query = {
          ...newSorted.reduce((acc, curr) => {
            acc[`sort.${curr.id}`] = curr.desc ? -1 : 1;
            return acc;
          }, {})
        };
        if (filters && filters.start_date && filters.end_date) {
          query.start_date = filters.start_date;
          query.end_date = filters.end_date;
        }
        if (filters && filters.department) {
          query.department = filters.department.map(({ value }) => value);
        }
        if (filters && filters.manager) {
          query.manager = filters.manager.map(({ value }) => value);
        }
        if (filters && filters.employment_status) {
          query.employment_status = filters.employment_status.map(
            ({ value }) => value
          );
        }
        if (filters && filters._id) {
          query._id = filters._id.map(({ value }) => value);
        }
        if (size || (filters && filters.pageSize)) {
          query.pageSize = typeof size === "number" ? size : filters.pageSize;
        }
        if (
          (pageNumber && typeof pageNumber === "number") ||
          (filters && typeof filters.page === "number")
        ) {
          query.page =
            typeof pageNumber === "number" ? pageNumber : filters.page;
        }
        query = queryString.stringify(query, { arrayFormat: "bracket" });
        const { data: result, pages: numPages } = await ReportService.get(
          `/pto-balance?${query}`
        );
        setData(result);
        setPages(numPages);
      } catch (err) {
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    },
    [filters, sorted]
  );

  const handleChange = (name) => (value) => {
    setLoading(false);
    setFilters({ ...filters, [name]: value });
  };

  const handleChangeDate = (startDate, endDate) => {
    setFilters((prev) => ({
      ...prev,
      ...(startDate && { start_date: moment(startDate).startOf("day") }),
      ...(endDate && { end_date: moment(endDate).endOf("day") })
    }));
  };

  const handleRowClick = () => {
    // history.push(`/app/pto/${rowInfo.original._id}`);
  };

  const handlePageSizeChange = async (pageSize) => {
    setFilters({ ...filters, pageSize });
    fetchData(null, pageSize);
  };

  const handlePageChange = async (page) => {
    setFilters({ ...filters, page });
    fetchData(page, null);
  };

  const handleExport = async () => {
    setLoading(true);
    let query = {
      groupByField: "customer",
      ...sorted.reduce((acc, curr) => {
        acc[`sort.${curr.id}`] = curr.desc ? -1 : 1;
        return acc;
      }, {})
    };
    if (filters && filters.start_date && filters.end_date) {
      query.start_date = filters.start_date;
      query.end_date = filters.end_date;
    }
    if (filters && filters.department) {
      query.department = filters.department.map(({ value }) => value);
    }
    if (filters && filters.manager) {
      query.manager = filters.manager.map(({ value }) => value);
    }
    if (filters && filters._id) {
      query._id = filters._id.map(({ value }) => value);
    }
    if (filters && filters.employment_status) {
      query.employment_status = filters.employment_status.map(
        ({ value }) => value
      );
    }
    query.pageSize = null;
    query.page = null;
    query = queryString.stringify(query, { arrayFormat: "bracket" });
    const { data: result } = await ReportService.get(`/pto-balance?${query}`);

    const title = `PTO Balance from ${moment(filters.start_date).format(
      "MM/DD/YYYY"
    )} to ${moment(filters.end_date).format("MM/DD/YYYY")}`;

    await downloadCSV(
      `PtoBalance-${formatDate(moment(), "yyyyMMdd")}`,
      result,
      PTO_BALANCE_COLUMNS,
      title
    );
    setLoading(false);
  };

  const handleSortedChange = (newSorted) => {
    setSorted(newSorted);
    fetchData(null, null, newSorted);
  };

  const handleResetFilters = () => {
    setFilters({
      page: 0,
      pageSize: 25,
      start_date: moment().startOf("year"),
      end_date: moment()
    });
    setData([]);
  };

  return (
    <div className="report-page">
      <Filters
        filters={filters}
        onChange={handleChange}
        focusedInput={focusedInput}
        onChangeDate={handleChangeDate}
        onFocusChange={(fi) => setFocusedInput(fi)}
        codes={codes}
        customers={customers}
        setProjects={setProjects}
        setCustomers={setCustomers}
        projects={projects}
        fetchData={fetchData}
        onExportClick={handleExport}
        handleResetFilters={handleResetFilters}
        exportDisabled={loading || !data?.length}
        loading={loading}
      />
      <PtoBalanceList
        data={data}
        loading={loading}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        page={filters && filters.page}
        pages={pages}
        pageSize={filters && filters.pageSize}
        sorted={sorted}
        handleExport={handleExport}
        onRowClick={handleRowClick}
        onSortedChange={handleSortedChange}
      />
    </div>
  );
};

ProjectSummaryPage.propTypes = {
  codes: PropTypes.array
};

export default ProjectSummaryPage;
